import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, RotateCcw, UserX } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Avatar from '../../components/ui/Avatar';
import Button from '../../components/ui/Button';
import ProgressBar from '../../components/ui/ProgressBar';
import Table from '../../components/ui/Table';
import { employees, stores, tasks, actionLogs, analyticsData } from '../../data/mockData';
import { useToast } from '../../components/ui/Toast';

const statusLabel = { online: 'Онлайн', offline: 'Офлайн', vacation: 'В отпуске' };
const actionTypeLabels = { scan: 'Сканирование', osg_check: 'ОСГ проверка', display_complete: 'Выкладка', photo_upload: 'Фото-отчёт', task_accept: 'Принята задача', task_cancel: 'Отмена' };

export default function EmployeeCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useState('stats');

  const emp = employees.find(e => e.id === id) || employees[0];
  const empStores = stores.filter(s => emp.storeIds.includes(s.id));
  const empTasks = tasks.filter(t => t.assignedTo === emp.id);
  const empLogs = actionLogs.filter(l => l.employeeId === emp.id);

  const tabs = [
    { key: 'stats', label: 'Статистика' },
    { key: 'tasks', label: 'Текущие задачи' },
    { key: 'log', label: 'Журнал действий' },
  ];

  const taskColumns = [
    { key: 'title', label: 'Название', render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
    { key: 'type', label: 'Тип', render: v => <Badge variant={v === 'osg' ? 'osg' : 'display'}>{v === 'osg' ? 'ОСГ' : 'Выкладка'}</Badge> },
    { key: 'priority', label: 'Приоритет', render: v => <Badge variant={v === 'urgent' ? 'urgent' : 'normal'}>{v === 'urgent' ? 'Срочный' : 'Обычный'}</Badge> },
    { key: 'status', label: 'Статус', render: v => <Badge variant={v}>{v === 'pending' ? 'Ожидает' : v === 'in_progress' ? 'В работе' : 'Завершено'}</Badge> },
    { key: 'itemCount', label: 'Товаров' },
    { key: 'progress', label: 'Прогресс', render: v => <ProgressBar value={v} showLabel /> },
    { key: 'dueDate', label: 'Дедлайн', render: v => <span style={{ fontSize: 12, color: '#6B7280' }}>{new Date(v).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span> },
  ];

  const logColumns = [
    { key: 'timestamp', label: 'Дата/время', render: v => <span style={{ fontSize: 12, color: '#6B7280' }}>{new Date(v).toLocaleString('ru-RU')}</span> },
    { key: 'type', label: 'Тип', render: v => <Badge variant={v === 'osg_check' ? 'osg' : v === 'display_complete' ? 'display' : 'primary'}>{actionTypeLabels[v] || v}</Badge> },
    { key: 'description', label: 'Описание', render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    { key: 'result', label: 'Результат', render: v => <Badge variant={v === 'success' ? 'success' : 'error'}>{v === 'success' ? 'Успешно' : 'Ошибка'}</Badge> },
    { key: 'photo', label: 'Фото', sortable: false, render: v => v ? <span style={{ fontSize: 18 }}>🖼️</span> : '—' },
  ];

  const statsData = analyticsData.dailyData.slice(-14).map((d, i) => ({ ...d, value: Math.floor(Math.random() * 15 + 2) }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button variant="ghost" icon={<ArrowLeft size={15} />} onClick={() => navigate('/employees')} size="sm">Назад</Button>
        <span style={{ color: '#D1D5DB' }}>/</span>
        <span style={{ fontSize: 13, color: '#6B7280' }}>{emp.name}</span>
      </div>

      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <Avatar name={emp.name} size={80} />
            <span style={{
              position: 'absolute', bottom: 4, right: 4, width: 14, height: 14,
              borderRadius: '50%', border: '3px solid #fff',
              background: emp.status === 'online' ? '#34C759' : emp.status === 'vacation' ? '#FF9500' : '#D1D5DB',
            }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1A1A2E' }}>{emp.name}</h2>
              <Badge variant={emp.status}>{statusLabel[emp.status]}</Badge>
              <Badge variant={emp.role === 'Старший продавец' ? 'primary' : 'gray'}>{emp.role}</Badge>
            </div>
            <div style={{ fontSize: 13, color: '#8E8E93', marginTop: 4 }}>{emp.email} • {emp.phone}</div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
              ID: <code style={{ background: '#F3F4F6', padding: '2px 6px', borderRadius: 4 }}>{emp.code}</code>
              &nbsp;• Работает с {new Date(emp.startDate).toLocaleDateString('ru-RU')}
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {empStores.map(s => <Badge key={s.id} variant="primary">{s.name}</Badge>)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" icon={<Edit size={14} />} size="sm" onClick={() => toast('Редактирование', 'success')}>Редактировать</Button>
            <Button variant="outline" icon={<RotateCcw size={14} />} size="sm" onClick={() => toast('Пароль сброшен', 'success')}>Сбросить пароль</Button>
            <Button variant="danger" icon={<UserX size={14} />} size="sm" onClick={() => toast('Деактивирован', 'warning')}>Деактивировать</Button>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: '#fff', padding: '6px', borderRadius: 12, width: 'fit-content', border: '1px solid #F0F0F0' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '7px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: tab === t.key ? 700 : 500,
            background: tab === t.key ? '#4A6CF7' : 'transparent',
            color: tab === t.key ? '#fff' : '#6B7280', fontFamily: 'inherit',
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'stats' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
            {[
              { label: 'Сканирований', value: 118, color: '#4A6CF7' },
              { label: 'ОСГ проверок', value: 20, color: '#FF6B6B' },
              { label: 'Выкладок', value: 16, color: '#FFBE0B' },
              { label: 'Среднее время', value: '22 мин', color: '#8B5CF6' },
              { label: '% выполнения', value: `${Math.round(emp.tasksCompleted / (emp.tasksCompleted + 3) * 100)}%`, color: '#34C759' },
            ].map(s => (
              <div key={s.label} style={{ background: '#fff', borderRadius: 12, padding: '16px', border: '1px solid #F0F0F0', textAlign: 'center' }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: '#8E8E93', marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
          <Card title="Активность за 14 дней">
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={statsData} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Line type="monotone" dataKey="value" stroke="#4A6CF7" strokeWidth={2} dot={false} name="Действий" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {tab === 'tasks' && (
        <Card padding="0"><Table columns={taskColumns} data={empTasks} emptyText="Нет активных задач" /></Card>
      )}

      {tab === 'log' && (
        <Card padding="0"><Table columns={logColumns} data={empLogs} emptyText="Нет действий" /></Card>
      )}
    </div>
  );
}
